import React, { useReducer } from 'react'
import {
  Button,
  Cell,
  DialogOverlay,
  DialogOverlayActions,
  Grid,
  InputGroup,
  Row,
  TextField,
  useOverlay,
} from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { deepCopy } from '../../../../util/Utils'
import { passThrough } from '../../../../services/toolsService'
import useNotifications from '../../../../hooks/useNotifications'

import { RawData } from '../../../common/RawData'

export default () => {}

export function RatesAction({ buttonText, path, method, datsJob, callback }) {
  const { t } = useTranslation()
  const { ratesState, ratesActions } = useRatesAction(path, method, datsJob, callback)

  return (
    <>
      <Button onClick={() => ratesActions.open()}>{t(`dats.${buttonText}`, `${buttonText}`)}</Button>
      <DialogOverlay {...ratesActions.dialogOverlay} title={t('dats.updateJobRates', 'Update Job Rates')}>
        <Grid>
          {/* Row */}
          <Cell columns={12}>
            <TextField
              label={t('dats.targetRequestsPerSecond', 'Target Requests Per Second')}
              value={ratesState.targetRequestsPerSecond}
              onChange={(event) => ratesActions.setTargetRequestsPerSecond(event.target.value)}
            />
          </Cell>

          {/* Row */}
          <Cell columns={12}>
            <TextField
              label={t('dats.actualRequestsPerSecond', 'Actual Requests Per Second')}
              value={ratesState.actualRequestsPerSecond}
              onChange={(event) => ratesActions.setActualRequestsPerSecond(event.target.value)}
            />
          </Cell>

          {/* Row */}
          <Cell columns={12}>
            <TextField
              label={t('dats.itemsPerRequest', 'Items Per Request')}
              value={ratesState.itemsPerRequest}
              onChange={(event) => ratesActions.setItemsPerRequest(event.target.value)}
            />
          </Cell>

          {/* Row */}
          <Cell columns={12}>
            <RawData state={ratesState} testId="ratesActionState" />
          </Cell>
        </Grid>

        <DialogOverlayActions>
          <InputGroup>
            <Row>
              <Button emphasis="high" onClick={() => ratesActions.onClickSubmit()}>
                Submit
              </Button>
              <Button emphasis="medium" onClick={() => ratesActions.onClickCancel()}>
                Cancel
              </Button>
            </Row>
          </InputGroup>
        </DialogOverlayActions>
      </DialogOverlay>
    </>
  )
}

function useRatesAction(path, method, datsJob, callback) {
  const { t } = useTranslation()
  const { error } = useNotifications()

  const initialState = getNewInitialState(
    datsJob.requestsPerSecond,
    datsJob.currentRequestsPerSecond,
    datsJob.itemsPerRequest
  )

  const reducer = (_, currentState) => deepCopy(currentState)

  const rates = useReducer(reducer, initialState)
  let ratesState = rates[0]
  const setRatesState = rates[1]

  const ratesActions = {
    dialogOverlay: useOverlay(),
    open: () => {
      ratesActions.dialogOverlay.open()
    },
    close: () => {
      ratesActions.dialogOverlay.close()
    },
    setTargetRequestsPerSecond: (targetRequestsPerSecond) => {
      ratesState.targetRequestsPerSecond = parseInt(targetRequestsPerSecond, 10)
      ratesActions.updateRatesState()
    },
    setActualRequestsPerSecond: (actualRequestsPerSecond) => {
      ratesState.actualRequestsPerSecond = parseInt(actualRequestsPerSecond, 10)
      ratesActions.updateRatesState()
    },
    setItemsPerRequest: (itemsPerRequest) => {
      ratesState.itemsPerRequest = parseInt(itemsPerRequest, 10)
      ratesActions.updateRatesState()
    },
    reset: () => {
      ratesState = getNewInitialState(
        datsJob.requestsPerSecond,
        datsJob.currentRequestsPerSecond,
        datsJob.itemsPerRequest
      )
      ratesActions.updateRatesState()
    },
    onClickSubmit: () => {
      const updatedJob = deepCopy(datsJob)
      updatedJob.requestsPerSecond = ratesState.targetRequestsPerSecond
      updatedJob.currentRequestsPerSecond = ratesState.actualRequestsPerSecond
      updatedJob.itemsPerRequest = ratesState.itemsPerRequest
      passThrough(path, method, updatedJob)
        .then(() => {
          callback()
          ratesActions.close()
        })
        .catch((err) => {
          ratesActions.reset()
          ratesActions.close()
          error(t('tools.errors.updatingRates', 'Error updating rates'), err)
        })
    },
    onClickCancel: () => {
      ratesActions.reset()
      ratesActions.close()
    },
    updateRatesState: () => {
      setRatesState(ratesState)
    },
  }

  return { ratesState, ratesActions }
}

function getNewInitialState(requestsPerSecond, currentRequestsPerSecond, itemsPerRequest) {
  return deepCopy({
    targetRequestsPerSecond: requestsPerSecond,
    actualRequestsPerSecond: currentRequestsPerSecond,
    itemsPerRequest,
  })
}
